// RosterReport.jsx

import { useEffect, useState } from "react";
import { Reactable } from "../../components/ui/reactable";
import { ReportPageShell } from "../../components/ui/reportPageShell";
import { ROSTERING_REPORT } from "../../constants/api";
import {
    formatTimeToAMPM,
} from "../../components/common/CommonFunctions";
import { cn } from "../../utils/tailwindUtils";
import dayjs from "dayjs";
import { EmployeeDetails } from "../../components/ui/cards";
import RosterBreadcrumb from "../roster/RosterBreadcrumb";

const RosterReport = ({ backTo = "/reports" }) => {
    const [rosterReportData, setRosterReportData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [dynamicColumns, setDynamicColumns] = useState([]);
    const [fetchFilter, setFetchFilter] = useState({});

    // Default selected dates (current week: Mon–Sun)
    const [selectedDate, setSelectedDate] = useState({
        from_date: dayjs().startOf("week").add(1, "day"),
        to_date: dayjs().endOf("week").add(1, "day"),
    });

    // Generate dynamic columns whenever from/to dates change
    useEffect(() => {
        const { from_date, to_date } = fetchFilter;

        if (!from_date || !to_date) {
            setDynamicColumns([]);
            return;
        }

        const startDate = dayjs(from_date).startOf("day");
        const endDate = dayjs(to_date).startOf("day");

        if (!startDate.isValid() || !endDate.isValid()) return;

        const totalDays = endDate.diff(startDate, "day") + 1;
        const newColumns = [];

        for (let i = 0; i < totalDays; i++) {
            const currentDay = startDate.add(i, "day");
            const formattedDate = currentDay.format("YYYY-MM-DD"); // key for matching data
            const displayDate = currentDay.format("DD-MM-YYYY"); // header text
            const dayOfWeek = currentDay.format("ddd"); // Short day name
            const isWeekend = currentDay.day() === 0 || currentDay.day() === 6; // Sunday or Saturday

            newColumns.push({
                accessor: formattedDate,
                Header: (
                    <div className="text-center py-2 px-2 min-w-[130px]">
                        {/* Day of Week */}
                        <div
                            className={cn(
                                "text-[0.7rem] font-semibold tracking-wide uppercase mb-1",
                                isWeekend
                                    ? "text-blue-600 dark:text-blue-400"
                                    : "text-gray-500 dark:text-gray-400"
                            )}
                        >
                            {dayOfWeek}
                        </div>

                        {/* Date */}
                        <div className="text-xs font-medium text-gray-800 dark:text-gray-100">
                            {displayDate}
                        </div>
                    </div>
                ),
                align: "center",
                minWidth: 130,
                maxWidth: 130,
                Cell: ({ row }) => {
                    const record = row;
                    const details = record?.shift?.find(
                        (item) => item.date === formattedDate
                    );

                    const shiftText = details?.is_weekoff
                        ? "Week Off"
                        : details?.is_leave
                            ? "Leave"
                            : details?.shift?.name || "-";

                    const getCellStyles = () => {
                        if (details?.is_weekoff) {
                            return {
                                container: "bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-700",
                                text: "text-red-800 dark:text-red-200",
                                time: "text-red-600 dark:text-red-300",
                                dot: "bg-red-500 dark:bg-red-400"
                            };
                        }
                        if (details?.is_leave) {
                            return {
                                container: "bg-orange-50 dark:bg-orange-900/30 border-orange-200 dark:border-orange-700",
                                text: "text-orange-800 dark:text-orange-200",
                                time: "text-orange-600 dark:text-orange-300",
                                dot: "bg-orange-500 dark:bg-orange-400"
                            };
                        }
                        if (details?.shift) {
                            return {
                                container: "bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-700",
                                text: "text-green-800 dark:text-green-200",
                                time: "text-green-600 dark:text-green-300",
                                dot: "bg-green-500 dark:bg-green-400"
                            };
                        }
                        return {
                            container: "bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700",
                            text: "text-gray-600 dark:text-gray-300",
                            time: "text-gray-500 dark:text-gray-400",
                            dot: "bg-gray-400 dark:bg-gray-600"
                        };
                    };

                    const styles = getCellStyles();

                    return (
                        <div
                            className={cn(
                                "p-2 flex flex-col justify-center items-center w-full min-h-[60px] border rounded-lg",
                                styles.container,
                                "transition-all duration-200 hover:shadow-md hover:scale-[1.01]"
                            )}
                        >
                            {/* Shift Name + Dot */}
                            <div className="flex items-center gap-2 mb-1">
                                <div
                                    className={cn("w-2 h-2 rounded-full", styles.dot)}
                                    aria-label={shiftText}
                                />
                                <div
                                    className={cn(
                                        "font-semibold text-xs text-center px-1 py-0.5",
                                        styles.text
                                    )}
                                >
                                    {shiftText?.length > 10 ? (
                                        <span className="cursor-help border-b border-dotted border-current">
                                            {shiftText.slice(0, 10)}...
                                        </span>
                                    ) : (
                                        shiftText
                                    )}
                                </div>
                            </div>

                            {/* Shift Timings */}
                            {details?.shift?.start_time && details?.shift?.end_time && (
                                <div
                                    className={cn(
                                        "text-[0.65rem] flex gap-2 font-medium text-center leading-tight",
                                        styles.time
                                    )}
                                >
                                    <div>{formatTimeToAMPM(details.shift.start_time)}</div>
                                    <div className="text-[0.5rem]">→</div>
                                    <div>{formatTimeToAMPM(details.shift.end_time)}</div>
                                </div>
                            )}
                        </div>
                    );
                },
            });
        }

        setDynamicColumns(newColumns);
    }, [fetchFilter?.from_date, fetchFilter?.to_date]);

    // Table column definitions
    const rosterReportColumns = [
        {
            accessor: "employee_name",
            Header: "Employee",
            sortable: true,
            fixed: true,
            filterable: true,
            maxWidth: "380px",
            Cell: ({ row }) => {
                const record = row;

                const empDetails = {
                    ...record?.employee,
                    designation_id: record?.employee?.designation
                        ? {
                            id: record?.employee.designation.id,
                            designation_name: record?.employee.designation.name,
                        }
                        : { id: 0, designation_name: "" },
                    first_name: record?.employee?.employee_name || "-",
                    id: record?.employee?.employee_id || "-",
                };

                return <EmployeeDetails details={empDetails} size="xs" />;
            },
        },
        ...dynamicColumns,
        {
            accessor: "total_working_hours",
            Header: "Total WHrs",
            fixed: true,
            align: "right",
            maxWidth: "100px",
            Cell: ({ value }) => value + ' Hrs' ?? "-",
        },
    ];

    const legend = (
        <div className="flex flex-wrap items-center gap-4 text-sm font-medium text-gray-700 dark:text-gray-300">
            <div className="flex items-center gap-2">
                <span className="h-3 w-3 rounded-full bg-red-500 dark:bg-red-400 shadow-sm" />
                <span>Week Off</span>
            </div>
            <div className="flex items-center gap-2">
                <span className="h-3 w-3 rounded-full bg-orange-500 dark:bg-orange-400 shadow-sm" />
                <span>Leave</span>
            </div>
            <div className="flex items-center gap-2">
                <span className="h-3 w-3 rounded-full bg-green-500 dark:bg-green-400 shadow-sm" />
                <span>Shift</span>
            </div>
        </div>
    );

    const isRosterContext = backTo === "/roster";

    return (
        <ReportPageShell
            title="Roster Report"
            count={totalRecords}
            backTo={backTo}
            headerExtra={legend}
        >
            {isRosterContext && (
                <RosterBreadcrumb
                    className="px-0 -mt-2 mb-2"
                    items={[
                        { label: "Published Rosters", href: "/roster" },
                        { label: "Roster Report" },
                    ]}
                />
            )}
            <Reactable
                columns={rosterReportColumns}
                data={rosterReportData}
                setData={setRosterReportData}
                setTotalCount={setTotalRecords}
                setFetchFilter={setFetchFilter}
                url={ROSTERING_REPORT}
                serverSide={true}
                rowSize="md"
                isCSVRequired={true}
                isPDFRequired={true}
                isFromToDateRequired={true}
                selectedDate={selectedDate}
            />
        </ReportPageShell>
    );
}

export default RosterReport;